import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { IconGripVertical } from '@tabler/icons-react'
import { ReactNode } from 'react'

interface DndItem {
  id: string
}

interface SortableItemProps<T extends DndItem> {
  item: T
  index: number
  children: (item: T, index: number) => ReactNode
}

function SortableItem<T extends DndItem>({ item, index, children }: SortableItemProps<T>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: isDragging ? '#f8f9fa' : 'transparent',
    marginBottom: '8px',
    padding: '4px',
    borderRadius: '4px',
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div
          {...listeners}
          style={{
            cursor: isDragging ? 'grabbing' : 'grab',
            display: 'flex',
            alignItems: 'center',
            // The centered grip moves by half of any card height change: never the scroll anchor.
            overflowAnchor: 'none',
          }}
        >
          <IconGripVertical size={12} color="gray" />
        </div>
        <div style={{ flex: 1 }}>{children(item, index)}</div>
      </div>
    </div>
  )
}

interface DndListProps<T extends DndItem> {
  items: T[]
  onReorder: (items: T[]) => void
  children: (item: T, index: number) => ReactNode
}

export function DndList<T extends DndItem>({ items, onReorder, children }: DndListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = items.findIndex((item) => item.id === active.id)
    const newIndex = items.findIndex((item) => item.id === over.id)

    const reorderedItems = arrayMove(items, oldIndex, newIndex)
    onReorder(reorderedItems)
  }

  if (items.length === 0) {
    return null
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
        {items.map((item, index) => (
          <SortableItem key={item.id} item={item} index={index} children={children} />
        ))}
      </SortableContext>
    </DndContext>
  )
}
